(ns pfo.staging.workspace
  (:require
    [clojure.string :as str]
    [reagent.core :as reagent :refer [atom]]
    [pfo.api :as api]
    [pfo.controller :as controller :refer [subscribe]]
    [pfo.staging.component :as staging]
    [pfo.staging.aark :as aark]
    [pfo.widget.icons :as icons]
    [pfo.widget.pdf :refer [pdf-view]]
    [pfo.util :as util]))

;; =============================================================================
;; Documents-page "Unfiled" review workspace — standalone route
;; (#/document/unfiled), decoupled from the main document list. It loads its own
;; data (the staging queue) via the :staging-queue controller. The reviewer
;; previews a document, selects one or more, and files them with AARK (#2928).
;; =============================================================================

;; -----------------------------------------------------------------------------
;; Shared "All Documents | Unfiled · N" tab bar, rendered on both the existing
;; Documents page and this workspace. URL-driven (each tab navigates), so the
;; two pages stay independent. ACTIVE is :all or :unfiled; ACTIONS, when given,
;; sit at the right end of the bar.
;; -----------------------------------------------------------------------------
(defn documents-page-tabs [_active & _]
  (let [items (subscribe :staging-queue)]
    (controller/dispatch :staging-queue :load nil)
    (fn [active & [actions]]
      [:div.o-tabs-and-content__topbar.staging-doc-tabs
       [:ul.o-tabs-and-content__tabs
        [:li.o-tabs-and-content__tab
         {:class    (when (= active :all) "is-active")
          :on-click #(util/set-location "#/document/list")}
         "All Documents"]
        [:li.o-tabs-and-content__tab
         {:class    (when (= active :unfiled) "is-active")
          :on-click #(util/set-location "#/document/unfiled")}
         [:span "Unfiled"
          (when-let [n (some-> @items count)]
            (when (pos? n)
              [:span.tab-count {:style {:margin-left "0.4em"}} n]))]]]
       (when actions
         [:div.staging-doc-tabs__actions actions])])))

;; -----------------------------------------------------------------------------
;; Preview helpers
;; -----------------------------------------------------------------------------
(defn- lower [s] (some-> s str str/lower-case))

(defn- pdf-doc? [item]
  (let [ext (lower (:extension item))
        ct  (lower (:content_type item))]
    (or (= ext "pdf") (and ct (str/includes? ct "pdf")))))

(defn- image-doc? [item]
  (contains? #{"png" "jpg" "jpeg" "gif" "webp" "bmp" "heic"} (lower (:extension item))))

(defn- ensure-preview!
  "Resolve (once, cached) the short-lived session-less preview URL for a staged
  document so it can be embedded inline."
  [preview-cache document-id]
  (when-not (contains? @preview-cache document-id)
    (swap! preview-cache assoc document-id {:loading? true})
    (controller/dispatch :staging-queue :preview-url
      {:document-id document-id
       :on-success  (fn [resp] (swap! preview-cache assoc document-id {:url (:url resp)}))
       :on-error    (fn [_] (swap! preview-cache assoc document-id {:error true}) false)})))

(defn- preview-body [item url zoom on-error current-page num-pages]
  (cond
    (pdf-doc? item)
    ^{:key url}
    ;; Zoom scales only the rendered page (canvas), not the pager — applied via
    ;; the --staging-zoom CSS var (see staging.scss).
    [:div.staging-preview-doc {:style {"--staging-zoom" @zoom}}
     ;; The pager lives in the preview header (v3 design), so hide pdf-view's
     ;; built-in one and let it sync page state into our atoms instead.
     ;; :on-error flips this entry to the fallback below instead of letting
     ;; pdf-view's legacy retry-by-reload fire (a staged doc may be a corrupt
     ;; or non-PDF file — PRD §2.4: show a download option, no silent fail).
     [pdf-view {:multi-page        true
                :hide-nav          true
                :current-page-atom current-page
                :num-pages-atom    num-pages
                :load-pdf          (fn [cb] (cb #js {:url url}))
                :on-error          on-error}]]

    (image-doc? item)
    [:div.staging-preview-doc {:style {"--staging-zoom" @zoom}}
     [:img {:src url}]]

    :else
    [:div.staging-preview-fallback.o-media
     [:p "Preview not available for this file type."]
     [:a {:href url :target "_blank" :rel "noopener"} "Open document in a new tab →"]]))

(defn preview-pane [item preview-cache]
  ;; current-page / num-pages are synced by pdf-view (its built-in pager is
  ;; hidden); we render the pager in the header next to the zoom controls.
  (let [zoom         (atom 1.0)
        current-page (atom 1)
        num-pages    (atom nil)]
    (fn [item preview-cache]
      (let [doc-id   (:id item)
            entry    (get @preview-cache doc-id)
            ;; Keep the resolved :url so the error fallback can still offer an
            ;; open-in-tab link; just flag the entry so the pdf viewer is replaced.
            on-error (fn [_] (swap! preview-cache update doc-id assoc :error true))]
        [:div.staging-preview-pane
         [:div.staging-preview-pane__header
          [:div
           [:div.staging-doc-name (:name item)]
           [staging/uploaded-meta item]]
          [:div.staging-preview-pane__controls
           ;; PDF pager, in the header next to zoom (v3 design). Only shown for a
           ;; multi-page-capable PDF once pdf-view has reported its page count.
           (when-let [page-count (and (pdf-doc? item) @num-pages)]
             [:div.staging-preview-pane__pager
              [:button.staging-zoom-btn {:title    "Previous page"
                                         :disabled (<= @current-page 1)
                                         :on-click #(when (> @current-page 1) (swap! current-page dec))}
               [icons/chevron-left]]
              [:span.staging-zoom-level (str @current-page "/" page-count)]
              [:button.staging-zoom-btn {:title    "Next page"
                                         :disabled (>= @current-page page-count)
                                         :on-click #(when (< @current-page page-count) (swap! current-page inc))}
               [icons/chevron-right]]])
           [:div.staging-preview-pane__zoom
            [:button.staging-zoom-btn {:title "Zoom out"
                                       :on-click #(swap! zoom (fn [z] (max 0.25 (- z 0.25))))}
             [icons/minus]]
            [:span.staging-zoom-level (str (js/Math.round (* 100 @zoom)) "%")]
            [:button.staging-zoom-btn {:title "Zoom in"
                                       :on-click #(swap! zoom (fn [z] (min 4 (+ z 0.25))))}
             [icons/plus]]
            [:button.staging-zoom-btn {:title "Fit width" :on-click #(reset! zoom 1.0)}
             [icons/fit-width]]
            [:button.staging-zoom-btn {:title "Reset" :on-click #(reset! zoom 1.0)}
             [icons/rotate-ccw]]]]]
         [:div.staging-preview-pane__body
          (cond
            (:error entry)    [:div.staging-preview-fallback.o-media
                               [:p "Preview not available for this document."]
                               (when-let [u (:url entry)]
                                 [:a {:href u :target "_blank" :rel "noopener"}
                                  "Open document in a new tab →"])]
            (:loading? entry) [:p.o-media "Loading preview…"]
            (:url entry)      [preview-body item (:url entry) zoom on-error current-page num-pages]
            :else             [:p.o-media "Select a document to preview."])]]))))

;; -----------------------------------------------------------------------------
;; "File with AARK" — one AARK conversation for the selected documents
;; -----------------------------------------------------------------------------
;; The server's limit (maxConversationDocuments).
(def max-selection 10)

(defn selection-problem
  "Why ROWS cannot go to AARK together, or nil. The server refuses the same
  cases; the button says so before the reviewer clicks."
  [rows]
  (cond
    (empty? rows)                            "Select one or more documents"
    (> (count rows) max-selection)           (str "Select at most " max-selection " documents")
    (< 1 (count (set (map :family_id rows)))) "Select documents of one family"))

(defn file-with-aark-button [_items _checked]
  (let [busy (atom false)]
    (fn [items checked]
      (let [rows    (filterv #(contains? @checked (:id %)) items)
            problem (selection-problem rows)]
        [:button.c-button.staging-file-aark-btn
         {:disabled (or @busy (some? problem))
          :title    problem
          :on-click (fn []
                      (reset! busy true)
                      (controller/dispatch :staging-queue :file-with-aark
                        {:document-ids (mapv :id rows)
                         :on-error     (fn [_] (reset! busy false))}))}
         (if (seq rows)
           (str "File with AARK™ (" (count rows) ")")
           "File with AARK™")]))))

;; -----------------------------------------------------------------------------
;; Left-hand review queue
;; -----------------------------------------------------------------------------
(defn- queue-card [item selected? on-select checked? on-check]
  (let [notes (aark/notes-for item)]
    [:li.staging-queue-card
     {:class    (when selected? "is-selected")
      :on-click on-select}
     [:div.staging-queue-card__row
      [:input.staging-queue-card__check
       {:type      "checkbox"
        :title     "Select for File with AARK"
        :checked   checked?
        ;; Ticking a box must not move the preview to this card.
        :on-click  #(.stopPropagation %)
        :on-change #(on-check (.. % -target -checked))}]
      [:div
       [:div.staging-doc-name (:name item)]
       [staging/uploaded-meta item]
       ;; General "couldn't classify" hint (any AARK notes) — no per-note detail.
       (when (seq notes)
         [:div.staging-queue-card__flags
          [:span.staging-flag "AARK™ unable to classify"]])
       ;; File with AARK claims the documents; another reviewer's selection that
       ;; contains one gets a 409, and this says why.
       (when-let [reviewer (:claimed_by_name item)]
         [:div.staging-queue-card__flags
          [:span.staging-flag (if (= (:claimed_by item) (api/get-auth-info :user-id))
                                "In review by you"
                                (str "In review by " reviewer))]])]]]))

(defn staging-display-rows
  "Apply the queue's search / principal filter and sort to the raw ITEMS,
  returning them in the order the reviewer sees. Shared by the queue list (to
  render) and the workspace (to auto-select the visible top). Takes plain
  values, not atoms."
  [items search sort-order principal-filter]
  (cond->> items
    (not (str/blank? search))
    (filter #(str/includes? (str/lower-case (str (:name %)))
                            (str/lower-case search)))
    principal-filter
    (filter #(= (:uploader_id %) principal-filter))
    true
    (sort-by :submitted_at)
    (= :newest sort-order)
    reverse))

(defn queue-pane [items selected-id search sort-order principal-filter on-select checked]
  (let [principals (->> items
                        (map (fn [i] [(:uploader_id i) (:uploader_name i)]))
                        (into {}))
        filtered (staging-display-rows items @search @sort-order @principal-filter)
        ;; File with AARK sends every checked document, so a filter must not
        ;; leave hidden ones checked.
        uncheck-hidden! (fn []
                          (let [visible (set (map :id (staging-display-rows items @search @sort-order
                                                                            @principal-filter)))]
                            (swap! checked #(set (filter visible %)))))]
    [:div.staging-queue-pane
     [:div.staging-queue-pane__header
      [:span.staging-queue-pane__title "Review queue"]
      [:span.staging-queue-pane__count (str (count items) " pending")]]
     [:input.staging-queue-search
      {:type "text"
       :placeholder "Search by document name…"
       :value @search
       :on-change #(do (reset! search (.. % -target -value)) (uncheck-hidden!))}]
     [:div.staging-queue-pane__controls
      [:select {:value (name @sort-order)
                :on-change #(reset! sort-order (keyword (.. % -target -value)))}
       [:option {:value "newest"} "Newest first"]
       [:option {:value "oldest"} "Oldest first"]]
      [:select {:value (str @principal-filter)
                :on-change #(let [v (.. % -target -value)]
                              (reset! principal-filter (when-not (str/blank? v) (js/parseInt v)))
                              (uncheck-hidden!))}
       [:option {:value ""} "Principal"]
       (for [[id nm] principals]
         ^{:key id} [:option {:value (str id)} nm])]]
     [:div.staging-queue-pane__select
      "Select: "
      [:a {:href "#" :on-click (util/handler #(reset! checked (set (map :id filtered))))} "All"]
      " | "
      [:a {:href "#" :on-click (util/handler #(reset! checked #{}))} "None"]]
     [:ul.staging-queue-list
      (doall
        (for [item filtered
              :let [id (:id item)]]
          ^{:key id}
          [queue-card item (= id @selected-id) #(on-select item)
           (contains? @checked id)
           #(swap! checked (if % conj disj) id)]))]]))

;; -----------------------------------------------------------------------------
;; The workspace: queue and preview
;; -----------------------------------------------------------------------------
(defn review-workspace [opts]
  (let [selected-id      (atom (:focus opts))
        checked          (:checked opts)
        search           (atom "")
        sort-order       (atom :newest)
        principal-filter (atom nil)
        preview-cache    (atom {})
        items            (subscribe :staging-queue)
        ;; select! is the single entry point for changing the selection: set the
        ;; atom, reflect it in the URL (?focus= via replaceState — no remount, so
        ;; the address bar stays shareable / refresh-safe), and prime the preview.
        select! (fn [item]
                  (reset! selected-id (:id item))
                  (util/set-location-params {:focus (str (:id item))} true)
                  (ensure-preview! preview-cache (:id item)))
        ;; Keep selection side effects OUT of render — run them in the lifecycle
        ;; (did-mount/did-update). Auto-select the top of the current view whenever
        ;; nothing valid is selected (initial load, a stale ?focus, or a document
        ;; that left the queue); otherwise just prime the resolved doc. Keyed off
        ;; the resolved row (not selected-id) so a selection that left the queue
        ;; jumps to the next item; firing only when nothing is selected means
        ;; re-sorting never re-selects — the card just moves. select!/ensure-preview!
        ;; are idempotent and reset! converges, so this never loops.
        sync-selection! (fn []
                          (let [rows    @items
                                display (staging-display-rows rows @search @sort-order @principal-filter)
                                current (some #(when (= (:id %) @selected-id) %) rows)]
                            (cond
                              (and (nil? current) (seq display)) (select! (first display))
                              current                            (ensure-preview! preview-cache (:id current)))))]
    (reagent/create-class
      {:component-did-mount  (fn [_]
                               (controller/dispatch :staging-queue :load nil)
                               (sync-selection!))
       :component-did-update (fn [_ _] (sync-selection!))
       :reagent-render
       (fn [_opts]
         (let [rows     @items
               selected (some #(when (= (:id %) @selected-id) %) rows)]
           (cond
             (nil? rows)
             [:div.staging-workspace-empty "Loading…"]

             (empty? rows)
             [:div.staging-workspace-empty "No documents awaiting review."]

             ;; No row resolves (e.g. search filtered everything out, or briefly
             ;; before did-update auto-selects) — show the queue with an empty
             ;; preview rather than a stale selection.
             (nil? selected)
             [:div.staging-workspace
              [queue-pane rows selected-id search sort-order principal-filter select! checked]
              [:div.staging-preview-pane]]

             :else
             [:div.staging-workspace
              [queue-pane rows selected-id search sort-order principal-filter select! checked]
              ;; Keyed by the selected doc so zoom and page state reset per document.
              ^{:key (str "preview-" (:id selected))}
              [preview-pane selected preview-cache]])))})))

(defn unfiled-page
  "Standalone page mounted by the :document-unfiled route. OPTS may carry
  :focus (a document id to pre-select in the queue)."
  [_opts]
  (let [checked (atom #{})
        items   (subscribe :staging-queue)]
    (fn [opts]
      [:div.staging-workspace-page
       [documents-page-tabs :unfiled [file-with-aark-button @items checked]]
       [review-workspace (assoc opts :checked checked)]])))
